/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Dispatch, SetStateAction } from "react";
import { UseFormSetValue } from "react-hook-form";
import { ApprovalMode, ExecutionPolicy } from "~/generated/graphql";
import {
  PlanData,
  PlanField,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { FormData } from "~/views/plans/PlanCreate/components/PlanCreateWizard/formConfig";
import {
  FormField,
  SelectedPlanEntity,
  StepState,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/types";
import { updateEntities } from "../../utils";

type Props = {
  stepState: StepState;
  updateStepState: Dispatch<Partial<StepState>>;
  setPlanData: Dispatch<SetStateAction<PlanData>>;
  setValue: UseFormSetValue<FormData>;
};

export type HandleFieldChange = (
  name: FormField,
  onChange: (value: string | boolean) => void,
  value: string | boolean
) => void;

export type HandleExecutionPolicyChange = (
  onChange: (value: string | boolean) => void,
  value: ExecutionPolicy
) => void;

export function usePlanSettingsFieldHandlers({
  stepState,
  updateStepState,
  setPlanData,
  setValue,
}: Props) {
  const updatePlanSettings = (updatedSetting: Partial<SelectedPlanEntity>) => {
    const updatedEntities = updateEntities(stepState, updatedSetting);

    updateStepState({
      entities: updatedEntities,
      selectedEntity: {
        ...stepState.selectedEntity,
        ...updatedSetting,
      },
    });
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.PLAN_SETTINGS]: {
        planEntities: updatedEntities,
      },
    }));
  };

  const handleChange: HandleFieldChange = (name, onChange, value) => {
    updatePlanSettings({ [name]: value });
    onChange(value);
  };

  const handleExecutionPolicyChange: HandleExecutionPolicyChange = (
    onChange,
    value
  ) => {
    if (
      value === ExecutionPolicy.Approval &&
      !stepState.selectedEntity?.approvalMode
    ) {
      updatePlanSettings({
        [FormField.EXECUTION_POLICY]: value,
        [FormField.APPROVAL_MODE]: ApprovalMode.AsScheduled,
      });
      setValue(FormField.APPROVAL_MODE, ApprovalMode.AsScheduled);
      onChange(value);
      return;
    }

    handleChange(FormField.EXECUTION_POLICY, onChange, value);
  };

  return {
    handleChange,
    handleExecutionPolicyChange,
  };
}
